import { useEffect, useState } from "react";
import { Plus, Sparkles, Clock } from "lucide-react";
import { toast } from "sonner";
import NowModal from "./NowModal";
import { ReadOnlyNote } from "@/components/app/AppShell";
import { api, cardCls } from "@/lib/api";

const NowTab = ({ reload, readOnly = false }) => {
  const [updates, setUpdates] = useState([]);
  const [modal, setModal] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const guard = (fn) => (readOnly ? () => toast.info("Admin view — read only") : fn);

  const load = async () => {
    const { updates } = await api("/now");
    setUpdates(updates);
    setLoaded(true);
  };

  useEffect(() => {
    load().catch(() => toast.error("Could not load updates."));
  }, []);

  const endNow = async (id) => {
    await api(`/now/${id}/end`, { method: "POST" });
    toast.success("Update ended");
    load();
    reload();
  };

  const current = updates.find((u) => u.active);
  const scheduled = updates.filter((u) => !u.active && !u.is_ended && u.start_at && new Date(u.start_at) > new Date());
  const fmtSchedule = (iso) => new Date(iso).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

  return (
    <div data-testid="now-tab">
      <h1 className="font-serif text-2xl font-medium leading-snug tracking-tight text-guinep-ink">What clients need to know right now</h1>
      <p className="mt-1.5 text-sm text-guinep-mute">Keep your page current without another social post.</p>
      {readOnly && <ReadOnlyNote />}

      <button
        data-testid="new-update-button"
        onClick={guard(() => setModal({}))}
        className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-guinep-deep py-3.5 text-sm font-semibold text-white transition-colors hover:bg-guinep-mid"
      >
        <Plus size={16} /> New update
      </button>

      <div className="mt-5 space-y-3">
        {current && (
          <div className={`${cardCls} p-4`} data-testid="now-current-card">
            <div className="flex items-center gap-2">
              <Sparkles size={13} className="text-guinep-mid" />
              <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-guinep-mid">{current.type_label}</span>
              <span className="ml-auto rounded-full bg-guinep-soft px-2.5 py-0.5 text-[10px] font-semibold text-guinep-deep">Live</span>
            </div>
            <p className="mt-2 font-serif text-base text-guinep-ink">{current.message}</p>
            <div className="mt-3 flex gap-2">
              <button data-testid="now-edit-button" onClick={guard(() => setModal(current))} className="rounded-lg border border-guinep-line px-4 py-2 text-xs font-semibold text-guinep-ink transition-colors hover:bg-guinep-soft">Edit</button>
              <button data-testid="now-end-button" onClick={guard(() => endNow(current.now_id))} className="rounded-lg border border-guinep-line px-4 py-2 text-xs font-semibold text-guinep-mute transition-colors hover:text-red-600">End Now</button>
            </div>
          </div>
        )}
        {loaded && !current && <p className="py-8 text-center text-sm text-guinep-mute" data-testid="now-empty">No current update.</p>}
      </div>

      {scheduled.length > 0 && (
        <div className="mt-6" data-testid="now-scheduled-list">
          <p className="text-sm font-semibold text-guinep-ink">Scheduled</p>
          <p className="mt-0.5 text-xs text-guinep-mute">These are hidden from your public page until their “Show from” time.</p>
          <div className="mt-2.5 space-y-2.5">
            {scheduled.map((u) => (
              <div key={u.now_id} className={`${cardCls} p-4`} data-testid="now-scheduled-card">
                <div className="flex items-center gap-2">
                  <Clock size={13} className="text-guinep-mute" />
                  <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-guinep-mute">{u.type_label}</span>
                  <span className="ml-auto rounded-full bg-guinep-soft px-2.5 py-0.5 text-[10px] font-semibold text-guinep-mid">Goes live {fmtSchedule(u.start_at)}</span>
                </div>
                <p className="mt-2 font-serif text-base text-guinep-ink">{u.message}</p>
                <div className="mt-3 flex gap-2">
                  <button data-testid={`now-scheduled-edit-${u.now_id}`} onClick={guard(() => setModal(u))} className="rounded-lg border border-guinep-line px-4 py-2 text-xs font-semibold text-guinep-ink transition-colors hover:bg-guinep-soft">Edit</button>
                  <button data-testid={`now-scheduled-end-${u.now_id}`} onClick={guard(() => endNow(u.now_id))} className="rounded-lg border border-guinep-line px-4 py-2 text-xs font-semibold text-guinep-mute transition-colors hover:text-red-600">End</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <NowModal open={modal !== null} existing={modal?.now_id ? modal : null} onClose={() => setModal(null)} onSaved={() => { load(); reload(); }} />
    </div>
  );
};

export default NowTab;
